import React, { useRef, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import MicroModal from "micromodal";
import ReCAPTCHA from "react-google-recaptcha";

import departamentos from "./Departamentos";
import { DocumentType } from "./DocumentType";
import { DatosFormInstitutoAPI } from "../services/api/LoginInstitucional";
import { CancelRequestController } from "../util/cancel-request-controller";
import ModalAlertas from "../components/ModalAlertas";
import PropTypes from "prop-types";
import { useQueryParams } from "../hooks/useQueryParams";
import getUrlParam from "../js/utilities/getUrl";

import DatePicker from "react-multi-date-picker";
import DatePanel from "react-multi-date-picker/plugins/date_panel";
import InputIcon from "react-multi-date-picker/components/input_icon";
import moment from 'moment';
import { useId } from "../hooks/use-id";

/**
 * Este componente se encarga de renderizar el formulario de acceso institucional de tipo Form. Su función principal es validar los campos y redireccionar el inicio de sesión. Este hace uso del componente ModalAlertas.
 */
const Form = (props) => {
  const { instituto, traducciones, tipoFormulario } = props;

  const { institucional, login, accesibilidad } = traducciones;
  const { form } = institucional;

  const [preloader, setPreloader] = useState(false);
  const [notificacionNombre, setNotificacionNombre] = useState(false);
  const [notificacionTipoDoc, setNotificacionTipoDoc] = useState(false);
  const [notificacionFecha, setNotificacionFecha] = useState(false);
  const [notificacionCedula, setNotificacionCedula] = useState(false);
  const [notificacionDepartamento, setNotificacionDepartamento] = useState(false);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [captchaValido, setCaptchaValido] = useState(null);
  const [mostrarMensajeCaptcha, setMostrarMensajeCaptcha] = useState(false);
  const [isDevelopment, setIsdevelopment] = useState(false);
  const [fecha, setFecha] = useState(moment().format('YYYY-MM-DD'));
  const location = useLocation();
  const params = useQueryParams();
  const inst = getUrlParam('inst').toString();
  const redirect = params.get('redirectto');
  const institutoConDocumento = ["comfacauca", "comfenalco_valle"];
  const isNotIDCard = institutoConDocumento.includes(inst);

  const refForm = useRef(null);

  const nombreRef = useRef(null);
  const tipoIdRef = useRef(null);
  const cedulaRef = useRef(null);
  const departamentoRef = useRef(null);
  const captcha = useRef(null);

  const nombreYapellido = useId('nombreYapellido');
  const inputTipoId = useId('inputTipoId');
  const cedula = useId('cedula');
  const departamento = useId('departamento');
  const inputFecha = useId('inputFecha');
  const captchaHelp = useId('captcha-help');

  const errorNombre = useId('error-nombre');
  const errorIdentificacion = useId('errorIdentificacion');
  const errorDoc = useId('errorDoc');
  const errorDepartamento = useId('errorDepartamento');

  useEffect(()=>{
    const origin = window?.location?.origin;
    const development = origin === 'https://makemake.com.co' ?   false : true;
    setIsdevelopment(development)
  },[])

  /**
   * Esta función se encarga de validar los campos del formulario y enviar los datos para luego redireccionar.
   * @param {Event} e 
   */
  const enviarDatosAcceso = (e) => {
    e.preventDefault();

    nombreRef.current.addEventListener("invalid", () => { setNotificacionNombre(true) });
    cedulaRef.current.addEventListener("invalid", () => { setNotificacionCedula(true) });
    if (departamentoRef.current) {
      departamentoRef.current.addEventListener("invalid", () => { setNotificacionDepartamento(true) });
    }

    if (tipoFormulario === "formDocumentType") {
      tipoIdRef.current.addEventListener("invalid", () => { setNotificacionTipoDoc(true) });
    }

    if (tipoFormulario === "formBirthDate") {
      const fechaActual = moment().format('YYYY-MM-DD');
      if (fecha === fechaActual) {
        setNotificacionFecha(true)
      }
    }

    if (refForm.current.reportValidity()) {
      if (isDevelopment === false) {
        if (captchaValido === true) {
          enviarDatos()
        } else {
          setMostrarMensajeCaptcha(true)
        }
      } else {
        enviarDatos()
      }

    }
  }

  const enviarDatos = async () => {
    setPreloader(true)
    setMostrarMensajeCaptcha(false)
    let abortController = new CancelRequestController();

    let tipoIngreso = '';
    if (location.pathname === '/institucional/formulario' && params.get('access') === 'app') {
      tipoIngreso = 'app';
    }
    const propiedades = {
      inst: instituto,
      nombre: nombreRef.current.value,
      tipoId: tipoFormulario === "formDocumentType" ? tipoIdRef.current.value : 'N/A',
      cedula: cedulaRef.current.value,
      departamento: departamentoRef.current ? departamentoRef.current.value : 'N/A',
      fechaNacimiento: tipoFormulario === "formBirthDate"  ? fecha :  'N/A',
      tipoIngreso: tipoIngreso,
      redirect
    }
    await DatosFormInstitutoAPI.enviarDatos({
      abortSignal: abortController.signal,
      data: propiedades
    }).then(respuesta => {
      const error = respuesta.status;

      if (error === 0) {
        setPreloader(false)
        if (mostrarModal) {
          MicroModal.show('modal-form-institucional', {
            awaitCloseAnimation: true
          });
        } else {
          setMostrarModal(true)
        }
        MicroModal.show('modal-form-institucional', {
          awaitCloseAnimation: true
        });
      } else {
        setPreloader(true)
        window.location.href = window.location.origin + respuesta.info;
      }

    }).catch(error => console.log(error));

    return () => {
      abortController.abort();
    }
  }

  /**
   * Esta función cambia el estado de setCaptchaValido en caso de que el captcha sea correcto
   */
  const onChange = () => {
    if (captcha.current.getValue()) {
      setCaptchaValido(true)
    }
  }

  /**
   * Funcion que actualiza el estado de fecha
   * @param {Object} fechasSeleccionadas - objeto que devuelve el calendario DatePicker
   */
  const manejarFecha = (fechasSeleccionadas) => {
    setNotificacionFecha(false)
    let date = '';
    date = Boolean(fechasSeleccionadas) ? fechasSeleccionadas.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')

    setFecha(date);
  }


  return (
    <>
      <div className="col-lg-9 mx-auto pt-3 pb-5">
        <h2 className="mt-3 mb-4 text-center pb-3">{form?.titulo || 'Ingresa tus datos'}</h2>

        <form
          className="row mx-0 my-3 align-items-end"
          ref={refForm}
          onSubmit={enviarDatosAcceso}
        >
          {/* nombre y apellido */}
          <div className="col-lg-6 mb-3 mb-lg-0">
            <p className="secondary-item">
              <strong>{form?.nombre?.label || 'Nombre y apellido*:'}</strong>
            </p>
            <p className="onbackground-third--color mm-redimir-terms">
              {form?.nombre?.descripcion || 'Escríbelos como aparecen en tu identificación'}
            </p>
            <label htmlFor={nombreYapellido} className="sr-only">
              {form?.nombre?.label || 'Nombre y apellido*:'}
            </label>
            <input
              type="text"
              ref={nombreRef}
              className="form-control focusable-secondary"
              id={nombreYapellido}
              placeholder={form?.nombre?.placeholder || "Escribe tu nombre y apellido aquí"}
              required
              onChange={() => setNotificacionNombre(false)}
              aria-invalid={notificacionNombre}
              aria-describedby={notificacionNombre ? errorNombre : undefined}
            />
            <span id={errorNombre} role={"alert"} aria-live="assertive" className="notificacion-form">
              {notificacionNombre ? form?.nombre?.alert || 'Debes escribir tu nombre y apellido' : ''}
            </span>
          </div>

          {/* tipo de documento */}
          {tipoFormulario === "formDocumentType" &&
            <div className="col-lg-6 mb-3 mb-lg-0 mt-lg-4">
              <p className="secondary-item">
                <strong>{form?.tipoId?.label || 'Tipo de identificación*:'}</strong>
              </p>
              <label htmlFor={inputTipoId} className="sr-only">
                {form?.tipoId?.label || 'Tipo de identificación'}
              </label>
              <select
                className="form-control focusable-secondary"
                id={inputTipoId}
                required
                ref={tipoIdRef}
                onChange={() => setNotificacionTipoDoc(false)}
                aria-invalid={notificacionTipoDoc}
                aria-describedby={notificacionTipoDoc ? errorIdentificacion : undefined}
              >
                <option hidden value="">
                  {form?.tipoId?.placeholder || 'Escoge el tipo de Identificación'}
                </option>
                {DocumentType.map(item => {
                  return (
                    <option key={item.id} value={item.text}>{item.text}</option>
                  )
                })}

              </select>
              <span id={errorIdentificacion} role={"alert"} aria-live="assertive" className="notificacion-form">
                {notificacionTipoDoc ? form?.tipoId?.alert || 'Selecciona el tipo de documento' : ''}
              </span>
            </div>
          }

          {/* cédula */}
          <div className="col-lg-6 mb-3 mb-lg-0">
            <p className="secondary-item">
              <strong>
                {isNotIDCard ?
                  form?.cedula?.label_doc || 'Documento de identificación*:'
                  :
                  form?.cedula?.label || 'Número de cédula*:'
                }
              </strong>
            </p>
            <label htmlFor={cedula} className="sr-only">
              {isNotIDCard ?
                form?.cedula?.label_doc || 'Documento de identificación*:'
                : 
                form?.cedula?.label || 'Número de cédula*:'
              }
            </label>
            <input
              type="number"
              ref={cedulaRef}
              className="form-control focusable-secondary"
              id={cedula}
              placeholder={
                isNotIDCard ?
                  form?.cedula?.placeholder_doc || "Escribe tu número de identificación"
                  :
                  form?.cedula?.placeholder || "Escribe tu número de cédula"
              }
              required
              onChange={() => setNotificacionCedula(false)}
              aria-invalid={notificacionCedula}
              aria-describedby={notificacionCedula ? errorDoc : undefined}
            />
            <span id={errorDoc} role={"alert"} aria-live="assertive" className="notificacion-form">
              {notificacionCedula ?
                isNotIDCard ?
                  form?.cedula?.alert_doc || "Debes escribir tu número de identificación"
                  :
                  form?.cedula?.alert || 'Debes escribir tu número de cédula'
                : ''
              }
            </span>
          </div>

          {/* departamento */}
          {
            tipoFormulario !== "formBirthDate" &&
            <div className="col-lg-6 mb-3 mb-lg-0 mt-lg-4">
              <p className="secondary-item">
                <strong>
                  {
                    isNotIDCard ?
                      form?.departamento?.label_doc || 'Departamento* (Departamento de expedición de documento):'
                      :
                      form?.departamento?.label || 'Departamento* (Departamento de expedición de cédula):'
                  }</strong>
              </p>
              <label htmlFor={departamento} className="sr-only">
                {isNotIDCard ?
                  form?.departamento?.label_doc || 'Departamento* (Departamento de expedición de documento):'
                  :
                  form?.departamento?.label || 'Departamento* (Departamento de expedición de cédula):'
                }
              </label>
              <select
                className="form-control focusable-secondary"
                id={departamento}
                required
                ref={departamentoRef}
                onChange={() => setNotificacionDepartamento(false)}
                aria-invalid={notificacionDepartamento}
                aria-describedby={notificacionDepartamento ? errorDepartamento : undefined}
              >
                <option hidden value="">
                  {form?.departamento?.placeholder || 'Escoge tu departamento'}
                </option>
                {departamentos.map(item => {
                  return (
                    <option key={item} value={item}>{item}</option>
                  )
                })}
              </select>
              <span id={errorDepartamento} role={"alert"} aria-live="assertive" className="notificacion-form">
                {notificacionDepartamento ? form?.departamento?.alert || 'Por favor, escoge un departamento' : ''}
              </span>
            </div>
          }


          {/* fecha de nacimiento */}
          {tipoFormulario === "formBirthDate" &&
            <div className="col-lg-6 mb-3 mb-lg-0 mt-lg-4">
              <p className="secondary-item">
                <strong>{form?.fechaNacimiento?.label || 'Fecha de nacimiento*:'}</strong>
              </p>
              <label id={inputFecha} className="sr-only" aria-hidden={true}>
                {form?.fechaNacimiento?.label || 'Fecha de nacimiento'}
              </label>
              <div className="w-100" aria-labelledby={inputFecha}>
                <DatePicker
                  className='purple bg-auto'
                  zIndex={10}
                  format={'YYYY-MM-DD'}
                  onChange={(x) => manejarFecha(x)}
                  maxDate={moment().format('YYYY-MM-DD')}
                  range={false}
                  multiple={false}
                  value={fecha}
                  showOtherDays
                  plugins={[
                    <DatePanel />
                  ]}
                  render={
                    <InputIcon placeholder={'YYYY-MM-DD'} className="w-100 form-control focusable-secondary" />
                  }

                />
              </div>
              <span role={"alert"} aria-live="assertive" className="notificacion-form">
                {notificacionFecha ? form?.fechaNacimiento?.alert || 'Por favor, elige tu fecha de nacimiento' : ''}
              </span>

            </div>
          }

          <p className="my-5 my-lg-0 p-2 p-lg-5 text-center mm-redimir-terms">
            {form?.terminos?.ingresar || 'Al ingresar tus datos estás aceptando'}
            <Link
              className="links-tyc secondary-item focusable-red"
              to="/terminos-y-condiciones"
            >
              {" "}{form?.terminos?.term_condiciones || 'Términos y condiciones'}
            </Link>
            {" "}{form?.terminos?.y || 'y'} {" "}
            <Link
              className="links-tyc secondary-item focusable-red"
              to="/politica-de-privacidad"
            >
              {form?.terminos?.politica || 'Política de privacidad'}
            </Link>
          </p>

          {isDevelopment === false &&
            <>
              <div aria-describedby={captchaHelp} className="w-100 d-flex justify-content-center">
                <ReCAPTCHA
                  ref={captcha}
                  sitekey="6LfaebskAAAAAF4jRB9WbC3pOOgmAaPZnOf-kn5-"
                  onChange={onChange}
                />
              </div>
              <p id="captcha-help" className="sr-only">
                {accesibilidad?.captcha || "Verifica que no eres un robot"}
              </p>
            </>
          }
          {mostrarMensajeCaptcha &&
            <span role={"alert"} aria-live="assertive" className="notificacion-form d-block w-100 mt-3 text-center">{form?.captcha || 'Por favor acepta el captcha'}</span>
          }

          <button
            className={`btn col-6 col-lg-4 d-block mx-auto focusable-red mt-5 ${preloader ? 'btn-disabled' : 'btn-primary'}`}
            type="submit"
            disabled={preloader ? true : false}
          >
            {preloader ?
              <>
                <span className="sr-only">
                  {accesibilidad?.enviando || "Enviando formulario"}
                </span>
                <i className="fas fa-spinner fa-pulse" aria-hidden={true}></i>
              </>
              :
              login?.botones?.enviar || "Enviar"
            }
          </button>

        </form>
      </div>

      {mostrarModal &&
        <ModalAlertas
          id={"modal-form-institucional"}
          alertType={form?.modal?.titulo || 'Alerta'}
          message={form?.modal?.mensaje || 'Error de acceso'}
          description={
            isNotIDCard ?
              form?.modal?.descripcion_doc || 'El nombre ingresado no coincide con el del documento'
              :
              form?.modal?.descripcion || 'El nombre ingresado no coincide con el de la cédula'
          }
          boton={login?.botones?.aceptar || "Aceptar"} />
      }
    </>
  )
}

export default Form;

Form.propTypes = {
  /**
   * String del nombre del instituto. Este valor se usa para los datos requeridos del api assets/formulario/loginFormulario
   */
  instituto: PropTypes.string.isRequired,
  /**
   *  Es un objeto que trae los textos de la interfaz dependiendo del idioma activo.
   */
  traducciones: PropTypes.object.isRequired,

  /**
   * String que se refiere al tipo de documento
   */
  tipoFormulario: PropTypes.string.isRequired
}